import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumb } from "../components/Breadcrumb";
import { Card, DashboardLayout } from "../components";
import { AdminVersionsResponse, adminApi } from "../services/api";
import {
  getWebBuildMetadata,
  WebBuildMetadata,
} from "../services/buildMetadata";

type BuildDetails = WebBuildMetadata | AdminVersionsResponse["api"];

function formatBuiltAt(value: string | null, unavailable: string) {
  if (!value) return unavailable;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function ExternalValue({
  value,
  href,
}: {
  value: string;
  href: string | null;
}) {
  return href ? (
    <a href={href} target="_blank" rel="noreferrer">
      <code>{value}</code>
      <i className="feather-external-link ms-1" aria-hidden="true"></i>
    </a>
  ) : (
    <code>{value}</code>
  );
}

function BuildCard({
  title,
  details,
  image,
}: {
  title: string;
  details: BuildDetails;
  image?: string;
}) {
  const { t } = useTranslation();
  const unavailable = t("versions.unavailable");
  const rows = [
    { label: t("versions.version"), value: <code>{details.version}</code> },
    {
      label: t("versions.commit"),
      value: <ExternalValue value={details.commit} href={details.commitUrl} />,
    },
    { label: t("versions.branch"), value: <code>{details.branch}</code> },
    {
      label: t("versions.repository"),
      value: <code>{details.repository}</code>,
    },
    {
      label: t("versions.builtAt"),
      value: formatBuiltAt(details.builtAt, unavailable),
    },
    {
      label: t("versions.ciRun"),
      value: details.ciRunUrl ? (
        <a href={details.ciRunUrl} target="_blank" rel="noreferrer">
          {t("versions.openCiRun")}
        </a>
      ) : (
        unavailable
      ),
    },
    {
      label: t("versions.changelog"),
      value: (
        <a
          href={details.changelogUrl ?? "/changelog"}
          target="_blank"
          rel="noreferrer"
        >
          {t("versions.openChangelog")}
        </a>
      ),
    },
    ...(image
      ? [
          {
            label: t("versions.image"),
            value: <code className="text-break">{image}</code>,
          },
        ]
      : []),
  ];

  return (
    <Card title={title} className="h-100">
      <dl className="row mb-0">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <dt className="col-sm-4 text-muted fw-normal py-2">{row.label}</dt>
            <dd className="col-sm-8 py-2 mb-0 text-break">{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </Card>
  );
}

export function AdminVersions() {
  const { t } = useTranslation();
  const [web, setWeb] = useState<WebBuildMetadata | null>(null);
  const [details, setDetails] = useState<AdminVersionsResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([getWebBuildMetadata(), adminApi.getVersions()])
      .then(([webMetadata, versionDetails]) => {
        if (!mounted) return;
        setWeb(webMetadata);
        setDetails(versionDetails);
      })
      .catch(() => {
        if (mounted) setError(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t("versions.title")}</h5>
          </div>
          <Breadcrumb
            items={[
              { label: t("admin.nav.home"), path: "/admin/home" },
              { label: t("versions.title") },
            ]}
          />
        </div>
      </div>

      <p className="text-muted mb-4">{t("versions.description")}</p>

      {error && (
        <div className="alert alert-danger" role="alert">
          {t("versions.loadError")}
        </div>
      )}
      {!error && (!web || !details) && (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("common.loading")}</span>
          </div>
        </div>
      )}

      {web && details && (
        <>
          <div className="row g-4 mb-4">
            <div className="col-xl-6">
              <BuildCard title={t("versions.web")} details={web} />
            </div>
            <div className="col-xl-6">
              <BuildCard
                title={t("versions.api")}
                details={details.api}
                image={details.api.image}
              />
            </div>
          </div>

          <Card title={t("versions.database")}>
            {details.database.status === "ok" ? (
              details.database.migration ? (
                <dl className="row mb-0">
                  <dt className="col-sm-3 text-muted fw-normal">
                    {t("versions.migration")}
                  </dt>
                  <dd className="col-sm-9 mb-2">
                    <code>{details.database.migration.name}</code>
                  </dd>
                  <dt className="col-sm-3 text-muted fw-normal">
                    {t("versions.migrationTimestamp")}
                  </dt>
                  <dd className="col-sm-9 mb-0">
                    <code>{details.database.migration.timestamp}</code>
                  </dd>
                </dl>
              ) : (
                <p className="mb-0 text-muted">{t("versions.noMigration")}</p>
              )
            ) : (
              <div className="alert alert-warning mb-0">
                {t("versions.databaseUnavailable")}
              </div>
            )}
          </Card>
        </>
      )}
    </DashboardLayout>
  );
}
